import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import useAxiosHook from '../../../../../../hooks/useAxiosHook';
import usePrevious from '../../../../../../hooks/usePrevious';
import { Courier, Region } from '../../../Regions.functions';

type Props = {
  isShown: boolean;
  selectedRegion: Region | undefined;
  selectedCourier: Courier | undefined;
  onHide: () => void;
  onDeleteSucess: (courierName: string, regionName: string) => void;
  onError: (courierName: string, regionName: string) => void;
};

function DeleteCourierDialog({
  isShown,
  selectedRegion,
  selectedCourier,
  onHide,
  onDeleteSucess,
  onError,
}: Props): JSX.Element {
  const { t } = useTranslation();

  const { data, error, reload, isLoading } = useAxiosHook();

  const prevData = usePrevious(data);
  const prevError = usePrevious(error);

  const courierName = `${selectedCourier?.first_name} ${selectedCourier?.last_name}`;

  useEffect(() => {
    if (!data || data === prevData) {
      return;
    }

    onDeleteSucess(courierName, selectedRegion?.name || '');
  }, [courierName, data, onDeleteSucess, prevData, selectedRegion]);

  useEffect(() => {
    if (!error || error === prevError) {
      return;
    }

    onError(courierName, selectedRegion?.name || '');
  }, [courierName, error, onError, prevError, selectedRegion]);

  function handleDeleteSubmision() {
    reload({
      url: `/regions/${selectedRegion?.region_id}/couriers/${selectedCourier?.id}`,
      method: 'DELETE',
    });
  }

  const dialogHeader = t('Are you sure?');

  const dialogFooter = (
    <>
      <Button
        type="button"
        label={t('Cancel')}
        onClick={onHide}
        className="p-button-text"
      />

      <Button
        type="button"
        label={isLoading ? t('Deleting...') : t('Delete')}
        disabled={isLoading}
        onClick={() => handleDeleteSubmision()}
        className="p-button-danger"
        data-cy="submit-btn"
      />
    </>
  );

  return (
    <Dialog
      visible={isShown}
      onHide={onHide}
      header={dialogHeader}
      footer={dialogFooter}
      resizable={false}
      dismissableMask
    >
      {t('You are about to remove {{courierName}} from {{selectedRegion}}.', {
        courierName: courierName,
        selectedRegion: selectedRegion?.name,
      })}{' '}
      <br />
      {t('This action cannot be undone.')}
      <br />
      {t('Are you sure?')}
    </Dialog>
  );
}

export default DeleteCourierDialog;
